import type {
  BprFinding,
  BprFrameworkContent,
  BprMenuValidationStatus,
  BprRecommendation,
} from '@protoss/shared';
import { readBprSimpleSynthesisMetadata, SALESFORCE_BPR_DRIVER_MAP } from '@protoss/shared';
import type { PillVariant } from '@protoss/ui';
import { Button, Pill } from '@protoss/ui';
import * as React from 'react';
import { bprApi } from '../services/bprApi';

export interface RecommendationDraft {
  recommendation: BprRecommendation | null;
  issueKey: string;
  patternKey: string;
  findingUuid?: string;
  title: string;
}

function validationStatusVariant(status?: BprMenuValidationStatus): PillVariant {
  switch (status) {
    case 'valid_match':
      return 'success';
    case 'valid_decline':
      return 'accent';
    case 'no_menu':
      return 'neutral';
    case 'invalid_match':
      return 'danger';
    default:
      return 'neutral';
  }
}

function validationStatusLabel(status?: BprMenuValidationStatus): string {
  switch (status) {
    case 'valid_match':
      return 'Valid Menu Match';
    case 'valid_decline':
      return 'Custom Architecture';
    case 'no_menu':
      return 'Custom Architecture (No Menu)';
    case 'invalid_match':
      return 'Invalid Menu Match (Unresolved)';
    default:
      return 'Standard Option';
  }
}

function recommendationStatusVariant(status: BprRecommendation['status']): PillVariant {
  switch (status) {
    case 'confirmed':
      return 'success';
    case 'rejected':
      return 'danger';
    case 'draft':
    default:
      return 'neutral';
  }
}

interface BprSimpleRecommendationsSectionProps {
  engagementUuid: string;
  recommendations: BprRecommendation[];
  findings: BprFinding[];
  framework: BprFrameworkContent | null;
  onEdit: (draft: RecommendationDraft) => void;
  onChanged: () => void;
}

export function BprSimpleRecommendationsSection({
  engagementUuid,
  recommendations,
  findings,
  framework: _framework,
  onEdit,
  onChanged,
}: BprSimpleRecommendationsSectionProps) {
  const [updatingUuid, setUpdatingUuid] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // Map findings by UUID for quick lookup
  const findingsMap = React.useMemo(() => {
    const map = new Map<string, BprFinding>();
    for (const f of findings) {
      map.set(f.uuid, f);
    }
    return map;
  }, [findings]);

  // Group recommendations by findingUuid
  const groupedByFinding = React.useMemo(() => {
    const groups = new Map<string, BprRecommendation[]>();
    const unlinked: BprRecommendation[] = [];

    for (const rec of recommendations) {
      if (rec.findingUuid) {
        const list = groups.get(rec.findingUuid) ?? [];
        list.push(rec);
        groups.set(rec.findingUuid, list);
      } else {
        unlinked.push(rec);
      }
    }

    return { groups, unlinked };
  }, [recommendations]);

  const handleSelectOption = async (
    targetRec: BprRecommendation,
    siblingRecs: BprRecommendation[]
  ) => {
    if (updatingUuid) return;
    setUpdatingUuid(targetRec.uuid);
    setError(null);
    try {
      const priorConfirmed = siblingRecs.find(
        (r) => r.uuid !== targetRec.uuid && r.status === 'confirmed'
      );
      await bprApi.updateRecommendation(engagementUuid, targetRec.uuid, {
        status: 'confirmed',
        ...(priorConfirmed ? { reselectPriorUuid: priorConfirmed.uuid } : {}),
      });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to select option.');
    } finally {
      setUpdatingUuid(null);
    }
  };

  const handleRejectOption = async (targetRec: BprRecommendation) => {
    if (updatingUuid) return;
    setUpdatingUuid(targetRec.uuid);
    setError(null);
    try {
      await bprApi.updateRecommendation(engagementUuid, targetRec.uuid, {
        status: 'rejected',
      });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject option.');
    } finally {
      setUpdatingUuid(null);
    }
  };

  if (recommendations.length === 0) {
    return (
      <div className="bpr-recommendations">
        <div className="bpr-section__toolbar">
          <h2 className="bpr-section__heading">Recommendations</h2>
        </div>
        <p className="bpr-section__empty">
          No recommendations yet. Run <code>/propose-solutions</code> to generate options for confirmed findings.
        </p>
      </div>
    );
  }

  return (
    <div className="bpr-recommendations">
      <div className="bpr-section__toolbar">
        <h2 className="bpr-section__heading">Recommendations ({recommendations.length})</h2>
      </div>

      {error ? <p className="bpr-dialog__error">{error}</p> : null}

      <div className="bpr-simple-recs">
        {/* Render grouped by finding */}
        {Array.from(groupedByFinding.groups.entries()).map(([findingUuid, recs]) => {
          const finding = findingsMap.get(findingUuid);
          return (
            <div key={findingUuid} className="bpr-simple-recs__group">
              <div className="bpr-simple-recs__group-header">
                <span className="bpr-simple-recs__group-label">Problem Finding:</span>
                <span className="bpr-simple-recs__group-title">
                  {finding?.currentStateSummary ?? `Finding ${findingUuid.slice(0, 8)}`}
                </span>
                {finding?.problemMeta?.priority ? (
                  <Pill
                    variant={
                      finding.problemMeta.priority === 'p1'
                        ? 'danger'
                        : finding.problemMeta.priority === 'p2'
                          ? 'accent'
                          : 'neutral'
                    }
                  >
                    {finding.problemMeta.priority.toUpperCase()}
                  </Pill>
                ) : null}
              </div>

              <div className="bpr-simple-recs__options-grid">
                {recs.map((rec) => {
                  const meta = readBprSimpleSynthesisMetadata(rec);
                  const isUpdating = updatingUuid === rec.uuid;
                  const isSelected = rec.status === 'confirmed';

                  return (
                    <div
                      key={rec.uuid}
                      className={`bpr-simple-recs__option-card${
                        isSelected ? ' bpr-simple-recs__option-card--selected' : ''
                      }`}
                    >
                      <div className="bpr-simple-recs__option-top">
                        <div className="bpr-simple-recs__option-badges">
                          {meta ? (
                            <Pill variant={validationStatusVariant(meta.menuValidationStatus)}>
                              {validationStatusLabel(meta.menuValidationStatus)}
                            </Pill>
                          ) : null}
                          <Pill variant={recommendationStatusVariant(rec.status)}>
                            {isSelected ? 'Selected' : rec.status}
                          </Pill>
                        </div>
                        <div className="bpr-simple-recs__option-hours">
                          {rec.estimatedHours != null ? (
                            <strong>{rec.estimatedHours}h</strong>
                          ) : (
                            <span className="bpr-simple-recs__unpriced">Unpriced</span>
                          )}
                        </div>
                      </div>

                      <h4 className="bpr-simple-recs__option-title">{rec.title}</h4>

                      {meta?.rationale ? (
                        <p className="bpr-simple-recs__option-rationale">{meta.rationale}</p>
                      ) : rec.futureStateProcess ? (
                        <p className="bpr-simple-recs__option-rationale">{rec.futureStateProcess}</p>
                      ) : null}

                      {meta?.declineReason ? (
                        <p className="bpr-simple-recs__option-decline">
                          <strong>Decline Rationale:</strong> {meta.declineReason}
                        </p>
                      ) : null}

                      {/* Typed Canonical Drivers */}
                      {meta?.drivers && Object.keys(meta.drivers).length > 0 ? (
                        <div className="bpr-simple-recs__drivers">
                          <span className="bpr-simple-recs__drivers-label">Drivers:</span>
                          <div className="bpr-simple-recs__drivers-list">
                            {Object.entries(meta.drivers).map(([key, val]) => {
                              const driverDef = SALESFORCE_BPR_DRIVER_MAP.find(
                                (d) => d.fieldKey === key || d.fullPath === key
                              );
                              const driverTitle = driverDef?.discoveryLabel ?? key;
                              return (
                                <span key={key} className="bpr-simple-recs__driver-chip">
                                  <strong>{driverTitle}:</strong> {String(val)}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}

                      {/* Actions */}
                      <div className="bpr-simple-recs__option-actions">
                        {!isSelected ? (
                          <Button
                            type="button"
                            size="sm"
                            disabled={isUpdating}
                            onClick={() => void handleSelectOption(rec, recs)}
                          >
                            {isUpdating ? 'Selecting…' : 'Select Option'}
                          </Button>
                        ) : null}
                        {rec.status !== 'rejected' && !isSelected ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isUpdating}
                            onClick={() => void handleRejectOption(rec)}
                          >
                            Reject
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            onEdit({
                              recommendation: rec,
                              issueKey: rec.issueKey,
                              patternKey: rec.solutionPatternKey ?? '',
                              findingUuid: rec.findingUuid ?? undefined,
                              title: rec.title,
                            })
                          }
                        >
                          Edit
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Unlinked recommendations */}
        {groupedByFinding.unlinked.length > 0 ? (
          <div className="bpr-simple-recs__group">
            <div className="bpr-simple-recs__group-header">
              <span className="bpr-simple-recs__group-title">Additional Recommendations</span>
            </div>
            <div className="bpr-simple-recs__options-grid">
              {groupedByFinding.unlinked.map((rec) => {
                const meta = readBprSimpleSynthesisMetadata(rec);
                return (
                  <div key={rec.uuid} className="bpr-simple-recs__option-card">
                    <div className="bpr-simple-recs__option-top">
                      {meta ? (
                        <Pill variant={validationStatusVariant(meta.menuValidationStatus)}>
                          {validationStatusLabel(meta.menuValidationStatus)}
                        </Pill>
                      ) : null}
                      <Pill variant={recommendationStatusVariant(rec.status)}>{rec.status}</Pill>
                    </div>
                    <h4 className="bpr-simple-recs__option-title">{rec.title}</h4>
                    {rec.futureStateProcess ? (
                      <p className="bpr-simple-recs__option-rationale">{rec.futureStateProcess}</p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
